/**
 * Validation Error Display Component
 *
 * Displays validation errors from expense report submission attempts.
 * Groups errors by category and provides actionable links to fix issues.
 */

import type { ValidationError } from '../../backend/services/expense-submission-validator.js';

export interface ValidationErrorDisplayProps {
  errors: ValidationError[];
  onFix?: (error: ValidationError) => void;
  className?: string;
}

/**
 * Error category definitions
 */
const ERROR_CATEGORIES = {
  REPORT: {
    title: 'Report Issues',
    icon: `<svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
    </svg>`,
    color: 'red'
  },
  RECEIPT: {
    title: 'Receipt Issues',
    icon: `<svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 14l6-6m-5.5.5h.01m4.99 5h.01M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16l7-3 7 3z" />
    </svg>`,
    color: 'amber'
  },
  PROFILE: {
    title: 'Payment Profile',
    icon: `<svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
    </svg>`,
    color: 'blue'
  }
} as const;

/**
 * Create validation error display component
 */
export function createValidationErrorDisplay(props: ValidationErrorDisplayProps): HTMLElement {
  const { errors, onFix, className = '' } = props;

  if (!errors || errors.length === 0) {
    return createEmptyState();
  }

  const container = document.createElement('div');
  container.className = `bg-red-900/20 border border-red-800/50 rounded-lg p-4 space-y-4 ${className}`;

  // Header
  const header = createHeader(errors.length);
  container.appendChild(header);

  // Group errors by category
  const groupedErrors = groupErrors(errors);

  // Display each category
  Object.entries(groupedErrors).forEach(([category, categoryErrors]) => {
    if (categoryErrors.length > 0) {
      const section = createCategorySection(category as keyof typeof ERROR_CATEGORIES, categoryErrors, onFix);
      container.appendChild(section);
    }
  });

  return container;
}

/**
 * Create validation error modal
 */
export function createValidationErrorModal(props: ValidationErrorDisplayProps & { onClose: () => void }): HTMLElement {
  const { errors, onFix, onClose } = props;

  // Create overlay
  const overlay = document.createElement('div');
  overlay.className = 'fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4';

  // Create modal
  const modal = document.createElement('div');
  modal.className = 'bg-neutral-900 border border-neutral-800 rounded-lg shadow-2xl max-w-lg w-full max-h-[80vh] overflow-hidden flex flex-col';

  // Modal header
  const modalHeader = document.createElement('div');
  modalHeader.className = 'p-6 border-b border-neutral-800 flex items-center justify-between';

  const title = document.createElement('h3');
  title.className = 'text-lg font-semibold text-neutral-100';
  title.textContent = 'Validation Errors';

  const closeBtn = document.createElement('button');
  closeBtn.className = 'text-neutral-400 hover:text-neutral-100 transition-colors';
  closeBtn.innerHTML = `
    <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
    </svg>
  `;
  closeBtn.onclick = onClose;

  modalHeader.appendChild(title);
  modalHeader.appendChild(closeBtn);
  modal.appendChild(modalHeader);

  // Modal content
  const content = document.createElement('div');
  content.className = 'flex-1 overflow-y-auto p-6';

  const displayProps: ValidationErrorDisplayProps = { errors };
  if (onFix) displayProps.onFix = onFix;
  const errorDisplay = createValidationErrorDisplay(displayProps);
  content.appendChild(errorDisplay);

  modal.appendChild(content);

  // Modal footer
  const footer = document.createElement('div');
  footer.className = 'p-6 border-t border-neutral-800 bg-neutral-900/50';

  const footerBtn = document.createElement('button');
  footerBtn.className = 'w-full px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-md transition-all';
  footerBtn.textContent = 'Fix Issues';
  footerBtn.onclick = onClose;

  footer.appendChild(footerBtn);
  modal.appendChild(footer);

  overlay.appendChild(modal);

  // Close on overlay click
  overlay.addEventListener('click', (e) => {
    if (e.target === overlay) {
      onClose();
    }
  });

  return overlay;
}

/**
 * Create empty state
 */
function createEmptyState(): HTMLElement {
  const container = document.createElement('div');
  container.className = 'bg-green-900/20 border border-green-800/50 rounded-lg p-4';

  const content = document.createElement('div');
  content.className = 'flex items-center gap-3';

  const icon = document.createElement('div');
  icon.className = 'text-green-500';
  icon.innerHTML = `
    <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  `;

  const message = document.createElement('span');
  message.className = 'text-sm text-green-400';
  message.textContent = 'No validation errors';

  content.appendChild(icon);
  content.appendChild(message);
  container.appendChild(content);

  return container;
}

/**
 * Create header
 */
function createHeader(errorCount: number): HTMLElement {
  const header = document.createElement('div');
  header.className = 'flex items-center gap-3';

  const icon = document.createElement('div');
  icon.className = 'text-red-500';
  icon.innerHTML = `
    <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  `;

  const text = document.createElement('div');
  text.className = 'flex-1';

  const title = document.createElement('h4');
  title.className = 'text-sm font-medium text-red-400';
  title.textContent = 'Cannot Submit Report';

  const subtitle = document.createElement('p');
  subtitle.className = 'text-xs text-neutral-400 mt-0.5';
  subtitle.textContent = `${errorCount} issue${errorCount !== 1 ? 's' : ''} must be resolved`;

  text.appendChild(title);
  text.appendChild(subtitle);

  header.appendChild(icon);
  header.appendChild(text);

  return header;
}

/**
 * Group errors by category
 */
function groupErrors(errors: ValidationError[]): Record<keyof typeof ERROR_CATEGORIES, ValidationError[]> {
  const grouped: Record<keyof typeof ERROR_CATEGORIES, ValidationError[]> = {
    REPORT: [],
    RECEIPT: [],
    PROFILE: []
  };

  errors.forEach(error => {
    if (error.code === 'NO_PAYMENT_PROFILE') {
      grouped.PROFILE.push(error);
    } else if (error.code.includes('RECEIPT') || error.field?.includes('receipt')) {
      grouped.RECEIPT.push(error);
    } else {
      grouped.REPORT.push(error);
    }
  });

  return grouped;
}

/**
 * Create category section
 */
function createCategorySection(
  category: keyof typeof ERROR_CATEGORIES,
  errors: ValidationError[],
  onFix?: (error: ValidationError) => void
): HTMLElement {
  const config = ERROR_CATEGORIES[category];
  const section = document.createElement('div');
  section.className = 'space-y-2';

  // Category header
  const header = document.createElement('div');
  header.className = 'flex items-center gap-2 mb-2';

  const icon = document.createElement('div');
  icon.className = `text-${config.color}-500`;
  icon.innerHTML = config.icon;

  const title = document.createElement('h5');
  title.className = 'text-sm font-medium text-neutral-300';
  title.textContent = config.title;

  header.appendChild(icon);
  header.appendChild(title);
  section.appendChild(header);

  // Error list
  const list = document.createElement('ul');
  list.className = 'space-y-1 pl-7';

  errors.forEach(error => {
    const item = createErrorItem(error, config.color, onFix);
    list.appendChild(item);
  });

  section.appendChild(list);

  return section;
}

/**
 * Create individual error item
 */
function createErrorItem(
  error: ValidationError,
  color: string,
  onFix?: (error: ValidationError) => void
): HTMLElement {
  const item = document.createElement('li');
  item.className = 'flex items-start gap-2';

  const bullet = document.createElement('span');
  bullet.className = `text-${color}-500 mt-0.5`;
  bullet.textContent = '•';

  const content = document.createElement('div');
  content.className = 'flex-1 space-y-1';

  const message = document.createElement('span');
  message.className = 'text-sm text-neutral-300';
  message.textContent = error.message;

  content.appendChild(message);

  // Add fix button if handler provided and error has a field
  if (onFix && error.field) {
    const fixBtn = document.createElement('button');
    fixBtn.className = `text-xs text-${color}-400 hover:text-${color}-300 underline transition-colors mt-0.5`;
    fixBtn.textContent = 'Fix this issue →';
    fixBtn.onclick = () => onFix(error);
    content.appendChild(fixBtn);
  }

  item.appendChild(bullet);
  item.appendChild(content);

  return item;
}

/**
 * Parse validation errors from API response
 */
export function parseValidationErrors(response: any): ValidationError[] {
  if (response?.validationErrors && Array.isArray(response.validationErrors)) {
    return response.validationErrors;
  }

  if (response?.error === 'Validation failed' && response?.message) {
    // Parse error message to extract individual errors
    const messages = response.message.split(', ');
    return messages.map((msg: string) => ({
      code: 'VALIDATION_ERROR',
      message: msg
    }));
  }

  return [{
    code: 'UNKNOWN_ERROR',
    message: response?.message || 'An unknown error occurred'
  }];
}